import type { QueryClient } from '@tanstack/react-query';

import { FIVE_SECONDS } from '@/constants/time';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { shouldFetchUserScoped } from '@/util/authTokens';

/**
 * Loads a signed-in reader's account record while the splash is still up.
 *
 * The open layout makes no routing decision until this record has arrived, because it
 * cannot yet tell whether the account still needs verification. Fetched after the splash,
 * that wait showed the signed-out landing page, sign-in button and all, to a reader who
 * was already signed in. Fetched before, the splash covers it and the first screen drawn
 * is the right one.
 *
 * Capped so a slow or unreachable API costs at most the cap on the splash: past it the
 * app opens and the layout waits as it did before. Never throws; the layout's own query
 * reports and retries a failure.
 */
export async function prefetchStartupUser(
  client: QueryClient,
  userId: string | undefined,
  timeoutMs: number = FIVE_SECONDS
): Promise<void> {
  if (!shouldFetchUserScoped(userId)) {
    return;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });

  try {
    await Promise.race([client.prefetchQuery(getCurrentUserV2QueryOptions(userId)), cap]);
  } finally {
    clearTimeout(timer);
  }
}
